import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from '@leagueplatform/locales';
import type {
  EnvironmentKey,
  EntityDetailSchema,
  ConfigEntityDetail,
  ConfigEntity,
  DomainEntity,
} from '@web-config-app/core';
import { isConfigEntity } from '@web-config-app/core';
import { captureError } from '@leagueplatform/observability';
import { isProduction } from '@leagueplatform/app-environment';
import { useLoadEntityRelationshipsSchemas } from '../use-load-entity-relationships-schemas/use-load-entity-relationships-schemas';
import { useFindConfigEntityByDomainId } from '../use-find-config-entity-by-domain-id/use-find-config-entity-by-domain-id';
import { useConfigEntityGetByIdQuery } from '../use-config-entity-get-by-id-query/use-config-entity-get-by-id-query';
import { useConfigEntity } from '../use-config-entity/use-config-entity';
import { useConfigApp } from '../use-config-app/use-config-app';
import { useAbortController } from '../use-abort-controller/use-abort-controller';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';
import type {
  EntityWithDependencies,
  DiscoveryFailure,
} from '../../utilities/get-all-referenced-entities/get-all-referenced-entities';
import type { BulkActionReportingStore } from '../../bulk-action-reporting/bulk-action-reporting.store';
import { useUserInfo } from '../use-user-info/use-user-info';
import { discoverDeepPublishTree } from './discover-deep-publish-tree';
import { executeDeepPublishLoop } from './execute-deep-publish';

/**
 * An entry in the deep publish tree, combining entity data with its
 * dependency graph and status information.
 */
export interface DeepPublishTreeEntry {
  entity: ConfigEntityDetail;
  dependencies: { id: string; domainEntityType: string }[];
  status: string;
  domainEntityType: string;
  entityName: string;
  link?: string;
}

export interface UseDeepPublishProps {
  entity: ConfigEntity | DomainEntity;
  environment: EnvironmentKey;
  schema: EntityDetailSchema;
  domainEntityType: string;
  bulkActionReportingStore: BulkActionReportingStore;
  getEntityDetailPath?: (
    entityType: string,
    instanceId: string,
  ) => string | undefined;
}

export interface UseDeepPublishResult {
  /** The discovered dependency tree (populated after discoverTree completes) */
  tree: DeepPublishTreeEntry[];
  /** Failures that occurred during tree discovery */
  failures: DiscoveryFailure[];
  /** Whether tree discovery is in progress */
  isDiscovering: boolean;
  /** Whether tree discovery encountered an error */
  isDiscoveryError: boolean;
  /** Error message from tree discovery */
  discoveryErrorMessage?: string;
  /** Whether publish execution is in progress */
  isExecuting: boolean;
  /** The ID of the root entity in the tree */
  rootEntityId?: string;
  /** Discover the dependency tree for the given entity */
  discoverTree: (rootEntityData: Partial<ConfigEntityDetail>) => Promise<void>;
  /** Execute deep publish on the discovered tree */
  executeDeepPublish: () => Promise<void>;
  /** Cancel the in-progress publish operation */
  cancelPublish: () => void;
  /** Reset the hook state */
  reset: () => void;
}

export const useDeepPublish = ({
  entity,
  environment,
  schema,
  domainEntityType,
  bulkActionReportingStore,
  getEntityDetailPath,
}: UseDeepPublishProps): UseDeepPublishResult => {
  const { formatMessage } = useIntl();
  const [tree, setTree] = useState<DeepPublishTreeEntry[]>([]);
  const [failures, setFailures] = useState<DiscoveryFailure[]>([]);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isDiscoveryError, setIsDiscoveryError] = useState(false);
  const [discoveryErrorMessage, setDiscoveryErrorMessage] = useState<
    string | undefined
  >();
  const [isExecuting, setIsExecuting] = useState(false);
  const [rootEntityId, setRootEntityId] = useState<string | undefined>();

  const { createAbortController, abort } = useAbortController();
  const { userInfo } = useUserInfo();
  const queryClient = useQueryClient();

  // Wrap the entity in a ConfigEntity to ensure publish operations use the
  // generic /v1/config-entities/{entityId} CACT proxy endpoint rather than
  // a domain-specific URL (e.g. /v1/config-campaigns/{id}).
  const domainEntityForConfig = isConfigEntity(entity)
    ? entity.domainEntity
    : entity;
  const configEntity = useConfigEntity({ domainEntity: domainEntityForConfig });

  // The base envelope schema (before domain-specific injection) is needed
  // by `executeDeepPublishLoop` to build per-entity-type config entities.
  const { configEntity: configEntityConfig } = useConfigApp();

  const loadEntityRelationshipsSchemas = useLoadEntityRelationshipsSchemas();
  const findEntityOnSourceByDomainEntityId = useFindConfigEntityByDomainId({
    environment,
  });

  const fetchEntityOnSourceById = useConfigEntityGetByIdQuery({
    sourceEnvironment: environment,
    domainEntityType,
    includeReferences: true,
  });

  const environmentFetch = useEnvironmentFetch(
    environment,
    configEntity.domainEntity.apiUrl,
  );

  // Keep a ref to the discovered entities map for use in executeDeepPublish
  const entitiesMapRef = useRef<Map<string, EntityWithDependencies>>(new Map());

  // Keep a ref to the schemas discovered during tree traversal
  const schemasMapRef = useRef<Map<string, EntityDetailSchema>>(new Map());

  // Track entity IDs that failed in the last run so retry only re-attempts those
  const failedEntityIdsRef = useRef<Set<string>>(new Set());

  const reset = useCallback(() => {
    setTree([]);
    setFailures([]);
    setIsDiscovering(false);
    setIsDiscoveryError(false);
    setDiscoveryErrorMessage(undefined);
    setIsExecuting(false);
    setRootEntityId(undefined);
    entitiesMapRef.current = new Map();
    schemasMapRef.current = new Map();
    failedEntityIdsRef.current = new Set();
  }, []);

  const discoverTree = useCallback(
    async (rootEntityData: Partial<ConfigEntityDetail>) => {
      setIsDiscovering(true);
      setIsDiscoveryError(false);
      setDiscoveryErrorMessage(undefined);
      setFailures([]);

      try {
        const result = await discoverDeepPublishTree({
          rootEntityData,
          schema,
          domainEntityType,
          loadEntityRelationshipsSchemas,
          findEntityOnSourceByDomainEntityId,
          fetchEntityOnSourceById,
          getEntityDetailPath,
        });

        entitiesMapRef.current = result.entitiesMap;
        schemasMapRef.current = result.schemasMap;
        setRootEntityId(result.rootEntityId);
        setFailures(result.failures);
        setTree(result.entries);
      } catch (error) {
        setIsDiscoveryError(true);
        const message =
          error instanceof Error ? error.message : 'Unknown error';
        setDiscoveryErrorMessage(message);

        if (isProduction()) {
          captureError(
            error instanceof Error ? error : new Error(String(error)),
            { errorName: 'DeepPublishDiscoveryError' },
          );
        }
      } finally {
        setIsDiscovering(false);
      }
    },
    [
      schema,
      domainEntityType,
      loadEntityRelationshipsSchemas,
      findEntityOnSourceByDomainEntityId,
      fetchEntityOnSourceById,
      getEntityDetailPath,
    ],
  );

  const executeDeepPublish = useCallback(async () => {
    const entitiesMap = entitiesMapRef.current;
    if (entitiesMap.size === 0) return;

    setIsExecuting(true);
    const abortController = createAbortController();

    const { failedEntityIds } = await executeDeepPublishLoop({
      entitiesMap,
      schemasMap: schemasMapRef.current,
      previouslyFailedIds: failedEntityIdsRef.current,
      configEntity,
      baseConfigEntitySchema: configEntityConfig.schema,
      domainEntityType,
      environment,
      environmentFetch,
      fetchEntityOnSourceById,
      userInfo,
      formatMessage,
      bulkActionReportingStore,
      abortSignal: abortController.signal,
    });

    if (abortController.signal.aborted) {
      setIsExecuting(false);
      return;
    }

    // Store failed IDs so the next retry only re-attempts those
    failedEntityIdsRef.current = failedEntityIds;

    // Invalidate all queries for this environment so the UI reflects
    // updated entity statuses (e.g. root entity shown as Published).
    queryClient.invalidateQueries({
      queryKey: [environment],
    });

    setIsExecuting(false);
  }, [
    configEntity,
    configEntityConfig.schema,
    domainEntityType,
    environment,
    environmentFetch,
    bulkActionReportingStore,
    createAbortController,
    fetchEntityOnSourceById,
    formatMessage,
    userInfo,
    queryClient,
  ]);

  const cancelPublish = useCallback(() => {
    abort();
    const store = bulkActionReportingStore.getState();
    if (store.session?.status === 'running') {
      store.completeSession();
    }
    setIsExecuting(false);
  }, [abort, bulkActionReportingStore]);

  return {
    tree,
    failures,
    isDiscovering,
    isDiscoveryError,
    discoveryErrorMessage,
    isExecuting,
    rootEntityId,
    discoverTree,
    executeDeepPublish,
    cancelPublish,
    reset,
  };
};
